import { withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment } from "@wordpress/element";
import { EditProps } from "blocks/types";
import React from "react";
import classnames from "classnames";
import { Icon } from "../../components";

import { useBlockStyle } from "../../hooks";

import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";

import "../assets/sass/blocks/latest-posts/style.scss";
import InspectorControls from "./components/InspectorControls";
import { useBlockProps } from "@wordpress/block-editor";
import { __ } from "@wordpress/i18n";
import { Button, Flex, FlexItem } from "@wordpress/components";
import { latestPosts } from "../../components/icon/icons.json";
import { upperFirst } from "lodash";
import { useSelect } from "@wordpress/data";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		className,
		attributes: {
			enableHeading,
			label,
			layout,
			column,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			layout3AdvancedStyle,
			layout4AdvancedStyle,
			layout5AdvancedStyle,

			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,

			excerptLimit,
			enableExcerpt,
			enableReadMore,
			readMoreText,

			enablePagination,

			metaPosition,
			enablePostTitle,
			enableAuthor,
			enableDate,

			hoverAnimation,
			hideOnDesktop,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "latest-posts",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	const blockProps = useBlockProps();

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const classNames = classnames(
		`mzb-latest-posts mzb-latest-posts-${clientId}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	const classNames2 = classnames(
		`mzb-posts mzb-${layout} mzb-post-col--${column || "4"}`,
		{
			[`mzb-${layout1AdvancedStyle}`]: layout === `layout-1`,
			[`mzb-${layout2AdvancedStyle}`]: layout === `layout-2`,
			[`mzb-${layout3AdvancedStyle}`]: layout === `layout-3`,
			[`mzb-${layout4AdvancedStyle}`]: layout === `layout-4`,
			[`mzb-${layout5AdvancedStyle}`]: layout === `layout-5`,
		}
	);

	const classNames3 = classnames(`mzb-post-heading mzb-${headingLayout}`, {
		[`mzb-${headingLayout1AdvancedStyle}`]:
			headingLayout === `heading-layout-1`,
		[`mzb-${headingLayout2AdvancedStyle}`]:
			headingLayout === `heading-layout-2`,
		[`mzb-${headingLayout3AdvancedStyle}`]:
			headingLayout === `heading-layout-3`,
		[`mzb-${headingLayout4AdvancedStyle}`]:
			headingLayout === `heading-layout-4`,
		[`mzb-${headingLayout5AdvancedStyle}`]:
			headingLayout === `heading-layout-5`,
		[`mzb-${headingLayout6AdvancedStyle}`]:
			headingLayout === `heading-layout-6`,
		[`mzb-${headingLayout7AdvancedStyle}`]:
			headingLayout === `heading-layout-7`,
		[`mzb-${headingLayout8AdvancedStyle}`]:
			headingLayout === `heading-layout-8`,
		[`mzb-${headingLayout9AdvancedStyle}`]:
			headingLayout === `heading-layout-9`,
	});

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{/* eslint-disable-next-line */}
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / 4)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author.id.toString(),
				label: author.name,
		  }))
		: [];

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				postTypeOptions={postTypeOptions}
				authorOptions={authorOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div>
				<div className={classNames}>
					{enableHeading === true && (
						<div className={`${classNames3}`}>
							<h2 className="mzb-heading-text"> {label} </h2>
						</div>
					)}
					<div className={classNames2}>
						<Fragment>
							{posts?.map((post, id) => {
								const maxWords = excerptLimit; // Replace with your desired word limit
								const excerpt = post?.excerpt?.rendered
									.split(" ")
									.slice(0, maxWords)
									.join(" ");
								return (
									<div key={id} className="mzb-post">
										{post
											?.magazine_blocks_featured_image_url
											?.full?.[0] && (
											<div
												className={`mzb-featured-image ${hoverAnimation}`}
											>
												<img
													src={
														post
															?.magazine_blocks_featured_image_url
															?.full[0]
													}
												/>
											</div>
										)}
										{(enableDate ||
											enableAuthor ||
											enablePostTitle) && (
											<>
												<div className="mzb-post-content">
													{metaPosition ===
														"bottom" && (
														<>
															{enablePostTitle ===
																true && (
																<h3 className="mzb-post-title">
																	<a
																		href={
																			"#"
																		}
																	>
																		{
																			post
																				?.title
																				?.rendered
																		}
																	</a>
																</h3>
															)}
															{(enableAuthor ||
																enableDate) && (
																<>
																	<div className="mzb-post-entry-meta">
																		{enableAuthor ===
																			true && (
																			<span className="mzb-post-author">
																				<img
																					className="author-display-image"
																					src={
																						post?.magazine_blocks_author_image
																					}
																				/>
																				<a
																					href={
																						"#"
																					}
																				>
																					{" "}
																					{
																						post
																							?.magazine_blocks_author
																							?.display_name
																					}{" "}
																				</a>
																			</span>
																		)}

																		{enableDate ===
																			true && (
																			<span className="mzb-post-date">
																				<Icon
																					type="blockIcon"
																					name="calendar"
																					size={
																						24
																					}
																				/>
																				<a href="#">
																					{" "}
																					{dateI18n(
																						__experimentalGetSettings()
																							.formats
																							.date,
																						post?.date_gmt,
																						undefined
																					)}{" "}
																				</a>
																			</span>
																		)}
																	</div>
																</>
															)}
														</>
													)}

													{metaPosition === "top" &&
														(enableAuthor ||
															enableDate) && (
															<>
																<div className="mzb-post-entry-meta">
																	{enableAuthor ===
																		true && (
																		<span className="mzb-post-author">
																			<img
																				className="author-display-image"
																				src={
																					post?.magazine_blocks_author_image
																				}
																			/>
																			<a
																				href={
																					"#"
																				}
																			>
																				{" "}
																				{
																					post
																						?.magazine_blocks_author
																						?.display_name
																				}{" "}
																			</a>
																		</span>
																	)}

																	{enableDate ===
																		true && (
																		<span className="mzb-post-date">
																			<Icon
																				type="blockIcon"
																				name="calendar"
																				size={
																					24
																				}
																			/>
																			<a href="#">
																				{" "}
																				{dateI18n(
																					__experimentalGetSettings()
																						.formats
																						.date,
																					post?.date_gmt,
																					undefined
																				)}{" "}
																			</a>
																		</span>
																	)}
																</div>

																{enablePostTitle ===
																	true && (
																	<h3 className="mzb-post-title">
																		<a
																			href={
																				"#"
																			}
																		>
																			{
																				post
																					?.title
																					?.rendered
																			}
																		</a>
																	</h3>
																)}
															</>
														)}

													{(enableExcerpt ||
														enableReadMore) && (
														<div className="mzb-entry-content">
															{enableExcerpt && (
																<div
																	className="mzb-entry-summary"
																	dangerouslySetInnerHTML={{
																		__html: excerpt,
																	}}
																/>
															)}
															{enableReadMore && (
																<div className="mzb-read-more">
																	<a
																		href={
																			"#"
																		}
																	>
																		{
																			readMoreText
																		}
																	</a>
																</div>
															)}
														</div>
													)}
												</div>
											</>
										)}
									</div>
								);
							})}
							{enablePagination && (
								<div className="mzb-pagination-numbers">
									{pagination}
								</div>
							)}
						</Fragment>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			offset,
			order = "desc",
			orderBy = "date",
			authorName,
			excludedCategory,
			page,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const selectedPostType = isTemplate ? "post" : postType || "post";

	const categories =
		getEntityRecords("taxonomy", "category", { per_page: -1 }) || [];

	const latestPosts: any[] = [];
	const fetchedPostIds = new Set();

	const shouldSkipFilters = isTemplate || selectedPostType !== "post";

	categories.forEach((cat: { id: number }) => {
		const query: Record<string, any> = {
			per_page: 1,
			offset: offset || 0,
			page,
			order,
			orderby: orderBy,
			author: authorName === "all" ? undefined : authorName,
		};

		if (!shouldSkipFilters) {
			query.categories = cat.id;

			if (excludedCategory && excludedCategory !== "") {
				query.categories_exclude = excludedCategory;
			}
			if (tag && tag !== "all") {
				query.tags = tag;
			}
			if (category && category !== "all") {
				query.categories = parseInt(category, 10);
			}
		}

		const postsInCategory = getEntityRecords(
			"postType",
			selectedPostType,
			query
		);

		if (postsInCategory && postsInCategory.length > 0) {
			const latestPost = postsInCategory[0];
			if (!fetchedPostIds.has(latestPost.id)) {
				latestPosts.push(latestPost);
				fetchedPostIds.add(latestPost.id);
			}
		}
	});

	return {
		posts: latestPosts,
		categories,
	};
})(Edit);
